import React from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';
import { SCHEMA_VALIDATION_MODELS } from '@eluve/llm-outputs';

import type { SchemaValidationResult } from '../useCreateTemplate';

interface SchemaValidationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isValidating: boolean;
  validationResults: SchemaValidationResult[];
}

export const SchemaValidationDialog: React.FC<SchemaValidationDialogProps> = ({
  open,
  onOpenChange,
  isValidating,
  validationResults,
}) => {
  const hasFailures = validationResults.some((r) => !r.accepted);
  const allPassed = validationResults.length > 0 && !hasFailures;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Template Schema Validation</DialogTitle>
          <DialogDescription>
            {isValidating
              ? 'Testing this template against the following models...'
              : allPassed
                ? 'All providers accepted the template schema.'
                : 'Some providers rejected the template schema.'}
          </DialogDescription>
        </DialogHeader>

        <Stack gap={2}>
          {isValidating
            ? SCHEMA_VALIDATION_MODELS.map(({ name }) => (
                <Stack key={name} gap={2} align="center" direction="horizontal">
                  <LoadingIcon size="lg" className="text-text-tertiary" />
                  <Text inline size="xs" color="tertiary">
                    {name}
                  </Text>
                </Stack>
              ))
            : validationResults.map((result) => (
                <Stack key={result.modelName} gap={1}>
                  <Stack gap={2} align="center" direction="horizontal">
                    {result.accepted ? (
                      <Icon
                        name="CheckCircle2"
                        size="lg"
                        className="text-text-positive"
                      />
                    ) : (
                      <Icon
                        name="CircleX"
                        size="lg"
                        className="text-text-negative-subtle"
                      />
                    )}
                    <Text inline size="xs" weight="semibold">
                      {result.modelName}
                    </Text>
                    <Text inline size="xs" color="tertiary">
                      {result.accepted ? 'Passed' : 'Failed'}
                    </Text>
                  </Stack>
                  {!result.accepted && result.error && (
                    <Text
                      size="xs"
                      color="secondary"
                      className="ml-6 break-all"
                    >
                      {result.error}
                    </Text>
                  )}
                </Stack>
              ))}
        </Stack>

        {!isValidating && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
